import { useCallback, useEffect, useState } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { platformApi } from '../api'
import { fieldIndex } from '../lib/form'
import type { AIView, FormDefinition, RiskLevel } from '../types'
import { SeverityBadge } from './badges'

const levelText: Record<RiskLevel, string> = { low: 'Low risk', medium: 'Medium risk', high: 'High risk' }

interface Props {
    submissionId: string
    definition: FormDefinition
    initial: AIView
}

/**
 * The reviewer's AI risk summary. Shown only to reviewers and managers. It polls
 * while a summary is being written and always says it is advisory.
 */
export function AiSummaryCard({ submissionId, definition, initial }: Props) {
    const [view, setView] = useState<AIView>(initial)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const status = view.summary?.status

    useEffect(() => setView(initial), [initial])

    // While the summary is being written, check back every couple of seconds.
    useEffect(() => {
        if (status !== 'pending') return
        const t = setInterval(() => {
            platformApi.aiSummary(submissionId).then(setView).catch(() => {})
        }, 2500)
        return () => clearInterval(t)
    }, [status, submissionId])

    const generate = useCallback(async () => {
        setBusy(true)
        setError(null)
        try {
            setView(await platformApi.regenerateAISummary(submissionId))
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not start the summary')
        } finally {
            setBusy(false)
        }
    }, [submissionId])

    if (!view.available) return null
    const rec = view.summary
    const s = rec?.summary
    const labels = fieldIndex(definition)

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                    AI risk summary
                    {status === 'ready' && s && <Badge variant="outline">{levelText[s.overall_risk]}</Badge>}
                </CardTitle>
                <CardDescription>A starting point for your review. It does not make the decision.</CardDescription>
                <CardAction>
                    <Button variant="outline" size="sm" onClick={generate} disabled={busy || status === 'pending'}>
                        {(busy || status === 'pending') && <Spinner data-icon="inline-start" />}
                        {rec ? 'Regenerate' : 'Generate'}
                    </Button>
                </CardAction>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

                {!rec && <p className="text-sm text-muted-foreground">No summary has been written for this request yet.</p>}
                {status === 'pending' && <p className="text-sm text-muted-foreground">Writing the summary. This usually takes a few seconds.</p>}
                {status === 'failed' && <p className="text-sm text-muted-foreground">{rec?.error || 'The summary could not be written.'}</p>}

                {status === 'ready' && s && (
                    <>
                        <div className="flex flex-col gap-1">
                            <p className="font-medium">{s.headline}</p>
                            <p className="text-sm">{s.summary}</p>
                        </div>

                        {s.concerns.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <h3 className="text-sm font-semibold">Things to look at</h3>
                                <ul className="flex flex-col gap-2" role="list">
                                    {s.concerns.map((c, i) => (
                                        <li key={i} className="flex flex-col gap-1 rounded-md border px-3 py-2 text-sm">
                                            <div className="flex items-center gap-2">
                                                <SeverityBadge severity={c.severity} />
                                                <span className="font-medium">{c.title}</span>
                                            </div>
                                            {c.detail && <p className="text-muted-foreground">{c.detail}</p>}
                                            {c.fields && c.fields.length > 0 && (
                                                <p className="text-xs text-muted-foreground">
                                                    Based on: {c.fields.map((k) => labels[k]?.label ?? k).join('; ')}
                                                </p>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {s.gaps.length > 0 && (
                            <div className="flex flex-col gap-1">
                                <h3 className="text-sm font-semibold">Missing or unclear</h3>
                                <ul className="ml-4 list-disc text-sm text-muted-foreground">{s.gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
                            </div>
                        )}

                        {s.questions_for_submitter.length > 0 && (
                            <div className="flex flex-col gap-1">
                                <h3 className="text-sm font-semibold">Questions you could ask</h3>
                                <ul className="ml-4 list-disc text-sm text-muted-foreground">{s.questions_for_submitter.map((q, i) => <li key={i}>{q}</li>)}</ul>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
            {status === 'ready' && rec?.generated_at && (
                <CardFooter className="text-xs text-muted-foreground">
                    Written by AI on {new Date(rec.generated_at).toLocaleString()}. Check it against the answers below.
                </CardFooter>
            )}
        </Card>
    )
}
